import {
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  MenuListProps,
  Select,
  SelectChangeEvent,
  SelectProps,
} from '@mui/material';
import {CSSProperties, useState, useId} from 'react';
import CheckIcon from '@mui/icons-material/Check';
import styles from './style.module.scss';
import {useFieldFocusStyle} from '../../../hooks/utils/useFieldFocusStyle';

export interface Option {
  key: string;
  value: string;
  label: string;
  budgetConsumptionEnabled?: boolean;
  displayElement?: JSX.Element;
  height?: number;
  roleCode?: string;
}

export interface OptionNode {
  key: string;
  value: string;
  label: string | React.ReactNode;
}

interface AurSelectProps {
  label: string;
  optionList: string[] | Option[] | OptionNode[]; // Support both strings and objects
  field: {
    value: string | undefined;
    onChange: (event: SelectChangeEvent<string>) => void;
    onBlur?: () => void;
  };
  fieldState: {
    error?: {
      message?: string;
    };
  };
  labelId?: string;
  onSelect?: (event: SelectChangeEvent<string>) => void;
  disabled?: boolean;
  helperText?: string;
  dataTestId?: string;
  isSingleSelection?: boolean;
  style?: CSSProperties;
  arialabel?: string;
  name?: string;
  menuProps?: Partial<SelectProps['MenuProps']>;
  onOpenDropdown?: () => void;
  menuListProps?: Partial<MenuListProps>;
}

export const AurSelect = (props: AurSelectProps) => {
  const {
    label,
    optionList,
    field,
    fieldState,
    labelId,
    onSelect,
    disabled = false,
    helperText = '',
    dataTestId,
    arialabel,
    name,
    isSingleSelection = false,
    style,
    onOpenDropdown,
    menuListProps = {},
  } = props;
  const [isOpen, setIsOpen] = useState(false); // State to track if dropdown is open
  const {borderClass, focusHandler, mouseKeyDownHandler} = useFieldFocusStyle({
    tabFocusClass: styles.tabFocusedBorder,
    mouseFocusClass: styles.mouseFocusedBorder,
  });

  const generatedId = useId().replace(/:/g, '');

  const internalLabelId = `aur-select-label-${generatedId}`;

  const selectId = `aur-select-${generatedId}`;

  const listboxId = `aur-select-listbox-${generatedId}`;

  const hasExternalLabel = Boolean(labelId);

  const hasInternalLabel = Boolean(label?.trim());

  const resolvedLabelId = hasExternalLabel
    ? labelId
    : hasInternalLabel
      ? internalLabelId
      : undefined;

  const fallbackAriaLabel = arialabel || label || name;

  const selected = field.value ?? undefined;
  const handleChange = (event: SelectChangeEvent<string>) => {
    field.onChange(event);
    if (onSelect) onSelect(event);
  };

  const checkmark = (option: Option | string | OptionNode): React.ReactNode => {
    if (!field?.value) return null;

    const isSelected = isSingleSelection
      ? isObjectList
        ? field.value === (option as Option).value
        : field.value === (option as string)
      : isObjectList
        ? field.value.indexOf((option as Option).value) > -1
        : field.value.indexOf(option as string) > -1;

    return isSelected ? (
      <CheckIcon fontSize="small" className="aurSelectIcon" />
    ) : null;
  };

  const isObjectList = typeof optionList[0] === 'object';

  return (
    <FormControl
      variant="filled"
      className={`aur-select-client ${borderClass}`}
      error={!!fieldState.error}
      disabled={disabled}
      style={style}
    >
      <div
        className={
          (isOpen ? 'drop-down-selected' : 'drop-down-not-selected') +
          ' aur-select-client'
        }
      >
        {!hasExternalLabel && hasInternalLabel && (
          <InputLabel
            id={internalLabelId}
            htmlFor={selectId}
            className={`select-label ${fieldState.error?.message && 'label-error'}`}
          >
            {label}
          </InputLabel>
        )}
        <Select
          id={selectId}
          labelId={resolvedLabelId}
          label={!hasExternalLabel ? label : undefined}
          name={name}
          value={
            selected === undefined ||
            selected === null ||
            optionList.length === 0
              ? ''
              : selected
          }
          disabled={disabled ? disabled : false}
          onChange={handleChange}
          className={`custom-select`}
          data-testid={dataTestId}
          onOpen={() => {
            setIsOpen(true);
            onOpenDropdown?.();
          }}
          onClose={() => setIsOpen(false)}
          inputProps={{
            /* * Use aria-label only when there is no visible label. * Do not provide aria-label and aria-labelledby together. */
            ...(resolvedLabelId
              ? {}
              : fallbackAriaLabel
                ? {'aria-label': fallbackAriaLabel}
                : {}),
          }}
          SelectDisplayProps={{
            /* * aria-controls should only exist while the controlled * listbox is rendered and open. */
            'aria-controls': isOpen ? listboxId : undefined,
          }}
          renderValue={(selected) => {
            if (isObjectList) {
              const selectedOption = (optionList as Option[]).find(
                (option) => option.key === selected
              );
              if (selectedOption) {
                return <>{(selectedOption as Option).label as string}</>;
              } else {
                return <></>;
              }
            } else {
              const selectedItem = (optionList as string[]).find(
                (option) => option === selected
              );
              return <>{selectedItem}</>;
            }
          }}
          MenuProps={{
            disableScrollLock: true,
            ...props.menuProps,
            PaperProps: {
              ...props.menuProps?.PaperProps,
              role: 'main',
              sx: {
                ...props.menuProps?.PaperProps?.sx,
                '& .MuiMenu-list': {
                  overflowY: 'auto',
                },
                '&::-webkit-scrollbar': {
                  width: '16px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#546A8C',
                  borderRadius: '100px',
                  border: '6px solid transparent',
                  backgroundClip: 'content-box',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#555',
                },
                scrollbarWidth: 'thin',
                scrollbarColor: '#546A8C transparent',
                border: '3px solid #546A8C !Important',
                borderRadius: '8px',
                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
              },
            },
            MenuListProps: {
              ...menuListProps,
              ...props.menuProps?.MenuListProps,
              /* * This is the ID referenced by aria-controls while open. */
              id: listboxId,
              /* * Give the listbox the same visible label as its combobox. */
              ...(resolvedLabelId
                ? {'aria-labelledby': resolvedLabelId}
                : fallbackAriaLabel
                  ? {'aria-label': fallbackAriaLabel}
                  : {}),
            },
          }}
          onFocus={focusHandler}
          onMouseDown={mouseKeyDownHandler}
        >
          {optionList.map((option) =>
            isObjectList ? (
              <MenuItem
                key={(option as Option).key}
                value={(option as Option).value}
              >
                <div className="dropdown-item-container">
                  {(option as Option).displayElement ? (
                    (option as Option).displayElement
                  ) : (
                    <ListItemText
                      primary={(option as Option).label as string}
                    />
                  )}
                  {checkmark(option)}
                </div>
              </MenuItem>
            ) : (
              <MenuItem key={option as string} value={option as string}>
                {option as string}
              </MenuItem>
            )
          )}
        </Select>
      </div>
      <FormHelperText>
        {!fieldState.error?.message && helperText}
      </FormHelperText>
      <FormHelperText className={fieldState.error?.message && 'label-error'}>
        {fieldState.error?.message}
      </FormHelperText>
    </FormControl>
  );
};
